import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { isNativeApp } from '../utils/platform';
import { inactivePlanCopy, uncoveredPlanLead } from '../shared/nativePurchase';
import { PACARION_POSTAL_ADDRESS } from '../shared/sponsorlessBrand';
import {
  homeownerPlanOffer,
  homeownerPlanChargeSentence,
  homeownerPlanCheckboxPlain,
  homeownerPlanCancelSentence,
  homeownerPlanButtonLabel,
} from '../shared/consentCopy';
import { startHomeownerCheckout, loadHomeownerBilling } from '../utils/homeownerApi';

const linkStyle = { color: '#0f766e', fontWeight: 650, fontSize: '1rem' };

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function screenPadding() {
  return {
    background: 'transparent',
    boxSizing: 'border-box',
    paddingTop: 'max(1.25rem, env(safe-area-inset-top, 0px))',
    paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))',
    paddingLeft: 'max(1.25rem, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(1.25rem, env(safe-area-inset-right, 0px))',
    minHeight: '100%',
  };
}

function NativePlanMessage({ colors }) {
  const copy = inactivePlanCopy(true);
  return (
    <p
      data-testid="native-plan-message"
      style={{
        margin: 0,
        color: colors.text,
        fontSize: '1.05rem',
        lineHeight: 1.55,
        textAlign: 'center',
      }}
    >
      {copy.message}
    </p>
  );
}

function planFromBilling(data) {
  if (!data || !data.plan) return null;
  return Object.assign({}, data.plan, { annualEnabled: data.annualEnabled === true });
}

export default function HomeownerPaywall({
  native,
  plan,
  onClose,
  startCheckout,
  subscribeLead,
  signup = false,
  page = false,
}) {
  const { colors, isDark } = useTheme();
  const onDevice = native === undefined ? isNativeApp() : native === true;
  const copy = inactivePlanCopy(onDevice);
  const hasPlan = Boolean(plan);
  const [loadedPlan, setLoadedPlan] = useState(null);
  React.useEffect(() => {
    if (hasPlan) return undefined;
    let cancel = false;
    loadHomeownerBilling()
      .then((data) => {
        if (!cancel) setLoadedPlan(planFromBilling(data) || { reason: 'unpaid', entitled: false });
      })
      .catch(() => {
        if (!cancel) setLoadedPlan({ reason: 'unpaid', entitled: false });
      });
    return () => {
      cancel = true;
    };
  }, [hasPlan]);
  const [billingInterval, setBillingInterval] = useState('month');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [canceledOn, setCanceledOn] = useState('');
  const view = loadedPlan || plan;
  const annualEnabled = Boolean(view && view.annualEnabled);
  const offer = homeownerPlanOffer(annualEnabled && billingInterval === 'year' ? 'year' : 'month');
  const monthly = homeownerPlanOffer('month');
  const annual = homeownerPlanOffer('year');
  const reason = view && view.reason;
  const portalReady = Boolean(view && view.portal);
  const open = Boolean(view && view.subscriptionOpen);
  const ending = Boolean(view && view.cancelAtPeriodEnd) || Boolean(canceledOn);

  const checkout = async () => {
    if (!accepted) {
      setError('Agree to the automatic charge before continuing.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const run = startCheckout || startHomeownerCheckout;
      const result = await run({
        action: 'checkout',
        interval: offer.interval,
        consent: { autoRenewal: true, kind: 'subscribe' },
      });
      if (result && result.url && copy.showPurchaseLink) {
        window.location.assign(result.url);
      }
    } catch (checkoutError) {
      setError((checkoutError && checkoutError.message) || 'Could not open checkout.');
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    setError('');
    try {
      const run = startCheckout || startHomeownerCheckout;
      const result = await run({ action: 'portal' });
      if (result && result.url && copy.showPortal) window.location.assign(result.url);
    } catch (portalError) {
      setError((portalError && portalError.message) || 'Could not open billing.');
    } finally {
      setBusy(false);
    }
  };

  const cancelPlan = async () => {
    setBusy(true);
    setError('');
    try {
      const run = startCheckout || startHomeownerCheckout;
      const result = await run({ action: 'cancel' });
      setCanceledOn((result && result.currentPeriodEnd) || (view && view.currentPeriodEnd) || 'end');
    } catch (cancelError) {
      setError((cancelError && cancelError.message) || 'Could not cancel the plan.');
    } finally {
      setBusy(false);
    }
  };

  if (!hasPlan && !view) {
    return <p style={{ color: colors.text, padding: '1.25rem' }}>Loading...</p>;
  }

  const covered = reason === 'sponsored' || reason === 'sponsorless_paid' || reason === 'sponsorless_free_period';
  if (!open && covered) {
    if (!page) return null;
    const paidThrough = view && view.currentPeriodEnd ? formatWhen(view.currentPeriodEnd) : '';
    const freeUntil = view && view.sponsorFreeUntil ? formatWhen(view.sponsorFreeUntil) : '';
    const statusLine = reason === 'sponsored'
      ? 'Your sponsor covers Pacarion for this home.'
      : reason === 'sponsorless_paid'
        ? (paidThrough ? `Your plan is active through ${paidThrough}.` : 'Your plan is active.')
        : (freeUntil ? `You can use Pacarion until ${freeUntil}.` : 'You can use Pacarion for now.');
    return (
      <section data-testid="subscription-status" aria-labelledby="subscription-status-title" style={{ color: colors.text, maxWidth: '520px' }}>
        <h1 id="subscription-status-title" style={{ fontSize: '1.6rem', margin: '0 0 8px' }}>Subscription</h1>
        <p style={{ margin: 0, lineHeight: 1.5 }}>{statusLine}</p>
      </section>
    );
  }

  if (copy.native && !open) {
    return (
      <div data-testid="homeowner-paywall" style={Object.assign({ color: colors.text, background: colors.background, minHeight: '100vh' }, screenPadding())}>
        <NativePlanMessage colors={colors} />
      </div>
    );
  }

  const grace = reason === 'grace';
  const box = {
    maxWidth: '560px',
    margin: '0 auto',
    background: colors.surface,
    color: colors.text,
    border: `1px solid ${isDark ? '#d1d5db' : '#1a1a1a'}`,
    borderRadius: '12px',
    padding: '1.25rem',
  };
  const choiceStyle = (selected) => ({
    flex: 1,
    minHeight: '64px',
    borderRadius: '10px',
    border: selected ? '2px solid #59c4c4' : `1px solid ${isDark ? '#d1d5db' : '#1a1a1a'}`,
    background: selected ? (isDark ? '#134e4a' : '#e7f7f7') : colors.surface,
    color: colors.text,
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
  });
  const primaryButton = {
    width: '100%',
    marginTop: '1rem',
    minHeight: '48px',
    border: 'none',
    borderRadius: '8px',
    background: '#59c4c4',
    color: '#083838',
    fontWeight: 750,
    fontSize: '1rem',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
  const periodEnd = canceledOn && canceledOn !== 'end'
    ? canceledOn
    : (view && view.currentPeriodEnd);

  if (open) {
    return (
      <div data-testid="homeowner-paywall" style={Object.assign({ color: colors.text, background: colors.background }, screenPadding())}>
        <div style={box}>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>Subscription</h1>
          {ending ? (
            <p data-testid="cancel-confirmation" style={{ fontSize: '1rem', lineHeight: 1.5 }}>
              Your plan is canceled. You keep access until {formatWhen(periodEnd) || 'the end of the paid period'}. It will not renew.
            </p>
          ) : (
            <>
              {grace ? (
                <p style={{ fontSize: '1rem', lineHeight: 1.5 }}>
                  Your payment didn't go through. You can keep using Pacarion until {formatWhen(view && view.graceUntil) || 'the grace period ends'}.
                </p>
              ) : (
                <p style={{ fontSize: '1rem', lineHeight: 1.5, marginTop: 0 }}>
                  Your plan renews until you cancel. You keep access through the end of the paid period.
                </p>
              )}
              <button
                type="button"
                onClick={cancelPlan}
                disabled={busy}
                style={primaryButton}
              >
                {busy ? 'Canceling...' : 'Cancel plan'}
              </button>
            </>
          )}
          {portalReady && copy.showPortal ? (
            <button
              type="button"
              onClick={openPortal}
              disabled={busy}
              style={{
                width: '100%',
                marginTop: '0.75rem',
                minHeight: '48px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Update your payment method
            </button>
          ) : null}
          {error ? <p role="alert" style={{ color: isDark ? '#fecaca' : '#991b1b' }}>{error}</p> : null}
          {onClose ? (
            <button type="button" onClick={onClose} style={{ width: '100%', marginTop: '0.75rem', minHeight: '44px', border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer' }}>
              Not now
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const neverSponsored = !(view && view.sponsorLinked);
  const lead = signup || neverSponsored
    ? "You don't have a sponsor yet. The Homeowner plan is $4.99 a month."
    : (subscribeLead || uncoveredPlanLead(annualEnabled));
  const showAnnual = annualEnabled && !signup;
  return (
    <div data-testid={signup ? 'homeowner-signup-plan' : 'homeowner-paywall'} style={Object.assign({ color: colors.text, background: colors.background }, screenPadding())}>
      <div style={box}>
        <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{signup ? 'Homeowner plan' : 'Your plan'}</h1>
        {grace ? (
          <p style={{ fontSize: '1rem' }}>
            Your payment didn't go through. You can keep using Pacarion until {formatWhen(view && view.graceUntil) || 'the grace period ends'}.
          </p>
        ) : (
          <p style={{ marginTop: 0, fontSize: '1rem', lineHeight: 1.5 }}>{lead}</p>
        )}
        {copy.showPrice ? (
          <div style={{ display: 'flex', gap: '0.75rem', margin: '1rem 0' }}>
            <button type="button" data-testid="plan-price" aria-pressed={offer.interval === 'month'} onClick={() => { setBillingInterval('month'); setAccepted(false); }} style={choiceStyle(offer.interval === 'month')}>
              ${monthly.priceText}/mo
            </button>
            {showAnnual ? (
              <button type="button" data-testid="plan-price" aria-pressed={offer.interval === 'year'} onClick={() => { setBillingInterval('year'); setAccepted(false); }} style={choiceStyle(offer.interval === 'year')}>
                ${annual.priceText}/yr
              </button>
            ) : null}
          </div>
        ) : null}
        <div
          data-testid="checkout-disclosure"
          style={{
            border: `1px solid ${isDark ? '#e5e7eb' : '#1a1a1a'}`,
            borderRadius: '10px',
            padding: '0.9rem 1rem',
            color: colors.text,
            background: colors.surface,
            fontSize: '1rem',
            lineHeight: 1.5,
            fontWeight: 650,
          }}
        >
          <p style={{ margin: '0 0 0.75rem' }}>{homeownerPlanChargeSentence(offer)}</p>
          <p style={{ margin: 0 }}>{homeownerPlanCancelSentence(offer)}</p>
        </div>
        <p style={{ fontSize: '1rem' }}>
          <a href="/trial-terms" style={linkStyle}>Auto-Renewal Terms</a>
          {' · '}
          <a href="/refunds" style={linkStyle}>Refund Policy</a>
          {' · '}
          <a href="/terms" style={linkStyle}>Terms</a>
        </p>
        <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', fontSize: '1rem', lineHeight: 1.45 }}>
          <input
            type="checkbox"
            checked={accepted}
            aria-label="Agree to automatic renewal"
            onChange={(event) => {
              setAccepted(event.target.checked);
              if (event.target.checked) setError('');
            }}
            style={{ marginTop: '0.25rem', width: '1.25rem', height: '1.25rem' }}
          />
          <span>{homeownerPlanCheckboxPlain(offer)}</span>
        </label>
        {error ? <p role="alert" style={{ color: isDark ? '#fecaca' : '#991b1b' }}>{error}</p> : null}
        <button
          type="button"
          onClick={checkout}
          disabled={busy || !accepted}
          style={Object.assign({}, primaryButton, {
            cursor: busy || !accepted ? 'not-allowed' : 'pointer',
            opacity: busy || !accepted ? 0.6 : 1,
          })}
        >
          {busy ? 'Opening checkout...' : homeownerPlanButtonLabel(offer)}
        </button>
        {onClose ? (
          <button type="button" onClick={onClose} style={{ width: '100%', marginTop: '0.75rem', minHeight: '44px', border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer' }}>
            {signup ? 'Set up my home' : 'Not now'}
          </button>
        ) : null}
        <p style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
          Pacarion LLC, {PACARION_POSTAL_ADDRESS}
        </p>
      </div>
    </div>
  );
}

export function HomeownerPlanBanner({ native }) {
  const { colors } = useTheme();
  const onDevice = native === undefined ? isNativeApp() : native === true;
  const [plan, setPlan] = React.useState(null);
  React.useEffect(() => {
    let cancel = false;
    if (typeof loadHomeownerBilling !== 'function') return undefined;
    loadHomeownerBilling()
      .then((data) => {
        if (!cancel) setPlan(planFromBilling(data));
      })
      .catch(() => {
        if (!cancel) setPlan(null);
      });
    return () => {
      cancel = true;
    };
  }, []);
  if (!plan || plan.entitled) {
    if (!(plan && plan.reason === 'grace')) return null;
  }
  if (onDevice) {
    if (plan && plan.subscriptionOpen) {
      return <HomeownerPaywall native plan={plan} />;
    }
    if (plan && plan.entitled) return null;
    return (
      <div style={{ margin: '0 0 1rem' }}>
        <NativePlanMessage colors={colors} />
      </div>
    );
  }
  if (plan && plan.entitled && plan.reason !== 'grace' && !plan.subscriptionOpen) return null;
  return <HomeownerPaywall native={false} plan={plan} />;
}

export { NativePlanMessage };
